"use client";

import { useGigya } from "@dxp-feed/gigya";
import { useState } from "@/utils/hooks/reactUseCustomHooks";
import { useRouter } from "next/navigation";
import { setLoginParams } from "@/utils/redux/gigya";
import { useDispatch } from "react-redux";
import { setToastMessage } from "@/utils/redux/recipes/toastMessageSlice";

const CACHE_KEY = "gigya_uid_cache";
const CACHE_DURATION_MS = 55 * 1000; // 55 seconds

export const useGigyaToken = () => {
  const { gigya, isLoaded } = useGigya();
  const router = useRouter();
  const [idToken, setIdToken] = useState<string | null>(null);
  const dispatch = useDispatch();

  const getAccountInfo = (): Promise<{ idToken: string; uid: string }> => {
    return new Promise((resolve) => {
      if (!gigya || !isLoaded) {
        console.warn("Gigya not ready");
        return resolve({ idToken: "", uid: "" });
      }

      const now = Date.now();
      try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        const parsed = cached ? JSON.parse(cached) : null;

        if (parsed?.idToken && now - parsed.cachedAt < CACHE_DURATION_MS) {
          setIdToken(parsed.idToken);
          return resolve({ idToken: parsed.idToken, uid: parsed.uid });
        }
      } catch (e) {
        console.warn("Gigya cache read failed:", e);
      }

      gigya.accounts.getAccountInfo({
        callback: (response: any) => {
          const token =
            response?.errorCode === 0 ? (response?.id_token ?? null) : null;
          const uid =
            response?.errorCode === 0 ? (response?.uid ?? null) : null;

          const newCache = {
            idToken: token,
            uid,
            cachedAt: Date.now(),
          };

          try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify(newCache));
          } catch (e) {
            console.warn("Gigya cache set failed:", e);
          }

          setIdToken(token);
          resolve({ idToken: token, uid });
        },
      });
    });
  };

  const resetLoginParams = async () => {
    router.push("/");
    router.refresh();
    dispatch(
      setToastMessage({
        message: "Session Timeout Please login again",
        status: "other",
      })
    );
  };

  return { idToken, getAccountInfo, resetLoginParams };
};
